import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { isEmpty, isUndefined, noop } from 'lodash';
import { Observable, of } from 'rxjs';
import { AppDispatcher } from '../../store/app.dispatcher';
import { BrowserManagerDataDispatcher } from '../browser-manager-data/browser-manager-data.dispatcher';
import { CookieService } from '../browser-manager-data/cookie.service';
import { NavigationService } from '../navigation/navigation.service';
import {
  defaultAppLocaleLanguage,
  supportedAppLocalesLanguages,
} from '../shared/constants/locales.constant';
import { ILocaleLanguage } from '../shared/interfaces/locale-language.interface';
import { DateTimeService } from 'i2g-ng-utils';
import { LoggerService } from '../shared/services/logger/logger.service';
import { LOCALIZATION_KEYS } from './localization.constants';
import { catchError, filter, map, share, take } from 'rxjs/operators';
import { TRANSLATION_REFRESH_KEY } from '../../app-translate.loader';
import { LocalStorageService } from '../browser-manager-data/local-storage.service';
import { ErrorService } from '../error-reporting/error.service';

@Injectable()
export class LocalizationService {
  private _country: string;

  constructor(
    private _translateService: TranslateService,
    private _loggerService: LoggerService,
    private _cookieService: CookieService,
    private _navigationService: NavigationService,
    private _dateTimeService: DateTimeService,
    private _appDispatcher: AppDispatcher,
    private _browserManagerDataDispatcher: BrowserManagerDataDispatcher,
    private _localStorageService: LocalStorageService,
    private _errorService: ErrorService,
    @Inject(DOCUMENT) private _document: Document
  ) {
    // this language will be used as a fallback when a translation isn't found in the current language
    this._translateService.setDefaultLang(defaultAppLocaleLanguage.locale);

    this._initLocale();
    this._initCountry();
  }

  isTranslationsReady(): Observable<boolean> {
    return isEmpty(this.getInstantTranslations(this.getLocale()))
      ? this._translateService.onLangChange.pipe(
          filter(
            (langChangeEvent: LangChangeEvent) =>
              langChangeEvent.lang === this.getLocale()
          ),
          map(
            (langChangeEvent: LangChangeEvent) =>
              !isUndefined(langChangeEvent.translations)
          ),
          take(1)
        )
      : of(true);
  }

  getLocale(): string {
    return this._translateService.currentLang;
  }

  setLocale(locale: string) {
    if (this._isSupportedLocale(locale)) {
      this._browserManagerDataDispatcher.setCookie(
        LOCALIZATION_KEYS.LOCALE,
        locale
      );

      setTimeout(() => this._navigationService.refresh());
    }
  }

  getParsedResult(
    translations: any,
    key: string,
    interpolateParams?: any
  ): any {
    return this._translateService.getParsedResult(
      translations,
      key,
      interpolateParams
    );
  }

  getParsedResultFromLang(
    lang: string,
    key: string,
    interpolateParams?: any
  ): any {
    return this._translateService.getParsedResult(
      this._translateService.translations[lang],
      key,
      interpolateParams
    );
  }

  getInstantTranslations(lang: string): any {
    const translations = this._translateService.translations[lang];

    if (!translations) {
      // notify raygun
      this._errorService.alertSystem(
        new Error(`Translations for ${lang} not found or empty`)
      );

      return this._translateService.translations[
        this._translateService.getDefaultLang()
      ];
    }

    return translations;
  }

  getTranslations(
    lang: string
  ): any | Observable<{ lang: string; translations: any }> {
    const req = this._translateService.getTranslation(lang).pipe(
      map((translations) => ({ lang, translations })),
      catchError(() => {
        lang = lang.split('-')[0];

        return this._translateService.getTranslation(lang).pipe(
          map((translations) => ({ lang, translations })),
          catchError(() => {
            const defaultLang = this._translateService.getDefaultLang();
            const translations = this.getInstantTranslations(defaultLang);

            if (translations) {
              return of({ lang: defaultLang, translations });
            } else {
              return this._translateService
                .getTranslation(defaultLang)
                .pipe(
                  map((translations) => ({ lang: defaultLang, translations }))
                );
            }
          })
        );
      }),
      share()
    );

    req.subscribe(noop, noop);

    return req;
  }

  getLoadedLangs(): string[] {
    return this._translateService.getLangs();
  }

  getDefaultLocale() {
    return this._translateService.getDefaultLang();
  }

  getBrowserCultureLang(): string {
    return this._translateService.getBrowserCultureLang();
  }

  getDecimalSeparator(locale: string = 'en_US'): string {
    locale = locale.replace('_', '-');

    const testNumber = 1.1;
    const formatter = global.Intl.NumberFormat(locale);
    const formattedNumber = formatter.format(testNumber);

    return formattedNumber.replace(/\d+/g, '');
  }

  getFormattedLocale(locale: string = 'en_US') {
    locale = locale.replace('_', '-');

    const supportedLocaleLanguage: ILocaleLanguage =
      supportedAppLocalesLanguages.find(
        (localeLanguageInfo: ILocaleLanguage) =>
          localeLanguageInfo.locale === locale
      ) || defaultAppLocaleLanguage;

    return supportedLocaleLanguage.locale;
  }

  private _initLocale() {
    let locale = this._cookieService
      .getCookie(LOCALIZATION_KEYS.LOCALE)
      .replace('_', '-');

    if (!locale) {
      locale = this._getSupportedLocale(locale);
      this._browserManagerDataDispatcher.setCookie(
        LOCALIZATION_KEYS.LOCALE,
        locale
      );
    }

    if (this._translateService.defaultLang !== locale) {
      this.getTranslations(locale)
        .pipe(take(1))
        .subscribe(({ lang }) => {
          if (lang !== locale) {
            this._browserManagerDataDispatcher.setCookie(
              LOCALIZATION_KEYS.LOCALE,
              lang
            );
          }

          this._dateTimeService.setDateLocale(lang);
          this._translateService.use(lang);
          this._localStorageService.removeItem(TRANSLATION_REFRESH_KEY); // clear cache if translations is ok
        });
    } else {
      this._dateTimeService.setDateLocale(locale);
      this._translateService.use(locale);
    }
  }

  private _isSupportedLocale(locale: string) {
    return supportedAppLocalesLanguages.some(
      (localeLanguage) => localeLanguage.locale === locale
    );
  }

  /**
   * Returns the best supported locale based on the following priorities:
   *  - case insensitive match
   *  - browser culture language match, e.g. "de-DE"
   *  - browser language match, e.g. "de"
   *  - if not found, fallback to default locale: "en-US"
   */
  private _getSupportedLocale(locale: string = ''): string {
    const browserCultureLang = this._translateService.getBrowserCultureLang();
    const browserLang = this._translateService.getBrowserLang();

    const supportedLocaleLanguage =
      supportedAppLocalesLanguages.find(
        (localeLanguage) =>
          localeLanguage.locale.toLowerCase() === locale.toLowerCase()
      ) ||
      supportedAppLocalesLanguages.find(
        (localeLanguage) => localeLanguage.locale === browserCultureLang
      ) ||
      supportedAppLocalesLanguages.find(
        (localeLanguage) => localeLanguage.locale.split('-')[0] === browserLang
      ) ||
      defaultAppLocaleLanguage;

    return supportedLocaleLanguage.locale;
  }

  private _initCountry() {
    this._country = this._cookieService.getCookie(LOCALIZATION_KEYS.COUNTRY);

    if (!this._country || !this._isSupportedCountry(this._country)) {
      this._country = this._getSupportedCountry(this._country);
      this._browserManagerDataDispatcher.setCookie(
        LOCALIZATION_KEYS.COUNTRY,
        this._country
      );
    }
  }

  private _isSupportedCountry(country: string) {
    return supportedAppLocalesLanguages.some(
      (localeLanguage) => localeLanguage.locale.split('-')[1] === country
    );
  }

  /**
   * Returns the best supported country based on the following priorities:
   *  - case insensitive match
   *  - browser culture language match, e.g. "de-DE"
   *  - if not found, fallback to default country: "US"
   */
  private _getSupportedCountry(country: string = ''): string {
    const browserCultureLang = this._translateService.getBrowserCultureLang();
    const supportedLocaleLanguage =
      supportedAppLocalesLanguages.find(
        (localeLanguage) =>
          localeLanguage.locale.split('-')[1] === country.toUpperCase()
      ) ||
      supportedAppLocalesLanguages.find(
        (localeLanguage) => localeLanguage.locale === browserCultureLang
      ) ||
      defaultAppLocaleLanguage;

    return supportedLocaleLanguage.locale.split('-')[1];
  }
}
